// <purpose>THE registry of HIDEABLE row signals (P2 Task R4, 2026-09-18) — the one list that says
// which hints a ticket-list row carries that a clerk (or a tenant default) may switch off, in the
// order the row renders them. Everything downstream reads this and nothing else: the Einstellungen
// grids, the preview, the usage survey, and the parity guard that holds the row to it.
//
// WHY A REGISTRY RATHER THAN A FLAG PER HINT: the ids are stored server-side as opaque strings
// (Task C6 — `user_ui_preferences.row_pills` and the tenant default `UI_ROW_PILLS_HIDDEN`), so the
// SPELLING of a kind is a contract with stored data. One file that owns every id, every German
// label and every applicability gate is what keeps a stored preference from silently meaning
// something else after a refactor, and what makes "which hints exist?" a question with one
// answer.</purpose>
// <dependencies>useCategoryMeta (type-only) — the served vocabulary is the source for the attribute
// kinds' existence AND their German labels; TICKET_ATTRIBUTES for the attribute PILL ORDER, which
// is this registry's order too.</dependencies>
// <constraints>NEVER HIDEABLE, and therefore never listed here (operator ruling, board 650688/01,
// 2026-09-18, Variant A): the handling badge, the decision past tense, the safety signal (the flag
// pill since part 5-2, which retired the override pill), the pending chip and the attachment glyph.
// The status pill (the model's one-line status) is RETIRED fleet-wide (part 6, 2026-10-07): pills
// are facets, never model-written summaries. Every never-hideable signal is DECLARED in the parity
// guard (components/__tests__/rowPillRegistryParity.test.tsx) rather than merely omitted, so a
// further signal cannot appear as undecided chrome.
// ⚠ Two more signals used to be adjudicated here — the received date and the decision date. Ruling
// R33 (2026-09-19) took BOTH off the rows outright (the arrival is a hover on the subject, the
// decision date lives on the detail's decision line), so there is nothing left to adjudicate:
// a row renders no date at all.
// NO HARDCODED ATTRIBUTE LIST: attribute kinds are DERIVED at read time from the tenant's served
// vocabulary, and their label is that attribute's served `labelDe`. A tenant that declares a third
// enum attribute gets a third switch with no code change; a tenant that declares none gets none.
// A kind whose `appliesWhen` fails is NOT LISTED, and an unlisted kind is treated as visible —
// there is no third state, so a stored preference naming a kind this tenant does not have buys
// nothing rather than hiding something else.</constraints>
import type { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { TICKET_ATTRIBUTES } from './ticketAttributes';
import type { FaceFn } from './categoryMeta';
import { FORM_WORD, FRIST_GENANNT_WORD } from '../constants';

/** One hideable signal, as the settings grids and the preview consume it. */
export interface RowPillKind {
  /** The stored id. Opaque to the backend; THIS file is where it is spelled. */
  id: string;
  /** The German noun a clerk reads beside the checkbox. Served where the tenant serves one. */
  labelDe: string;
}

/** The namespace every vocabulary-derived attribute kind lives in — `attribute:<served name>`. */
export const ATTRIBUTE_KIND_PREFIX = 'attribute:';

/** The one place an attribute kind's id is composed, so the settings page and the row cannot
 *  disagree about its spelling (and so the prefix is never re-typed). */
export const attributeKindId = (attributeName: string): string => `${ATTRIBUTE_KIND_PREFIX}${attributeName}`;

interface RowPillKindDef {
  id: string;
  /** The narrowest dependency this def's label actually needs: the served FACE resolver alone
   *  (`useCategoryMeta().face`), typed here as `FaceFn` (categoryMeta.ts, the resolver's home)
   *  rather than the whole `useCategoryMeta()` surface. A def that needs more of the tenant's
   *  vocabulary (`process_type`'s noun, an attribute's declared label) reads it by CLOSING OVER the
   *  `cm` its building function already received, below — those defs ignore this argument. */
  labelDe: (face: FaceFn) => string;
  /** True for the one def whose label reads the served FACE family — `resolveFace` (and so `face`
   *  itself) has no idea whether /config is still loading, so it answers the RAW KEY floor exactly
   *  as it would for a genuinely absent family (fix wave 2026-09-27, item 8). `rowPillKinds` below
   *  reads this flag to floor to '' instead while `cm.configLoading`, rather than let this settings
   *  list paint the key `receipt` a beat before the served word lands. Every other def's label is
   *  either a plain code constant or reads `cm.attribute`/`cm.secondaryAxisNoun` directly, neither
   *  of which shares this failure mode. */
  readsFace?: boolean;
  /** Absent = this kind exists for every tenant. */
  appliesWhen?: (cm: ReturnType<typeof useCategoryMeta>) => boolean;
}

/**
 * Every def, freshly built against THIS render's `cm` — a function of `cm` rather than a module-level
 * constant (final review, part 4c, 2026-09-27) so a def needing more than the face resolver
 * (`process_type`'s served noun, an attribute's declared label) can close over the real `cm` instead
 * of receiving it as a second, differently-typed parameter. IN ROW ORDER, which is also the order
 * every grid and the survey table render in.
 */
function buildRowPillKindDefs(cm: ReturnType<typeof useCategoryMeta>): readonly RowPillKindDef[] {
  // The attribute kinds, derived from the PILL-ORDER binding table rather than from the vocabulary's
  // own array order: the row paints attributes in TICKET_ATTRIBUTES order, and a settings list in a
  // different order from the row it describes is a small lie a reader has to work around.
  // Scoped to BOUND attributes for a harder reason than tidiness: only a bound attribute has a
  // per-ticket field and therefore a pill, so an unbound one would be a switch that hides nothing.
  // `type === 'enum'` is the second half of that gate — a scalar/boolean attribute (is_reminder)
  // carries no pill either.
  const attributeDefs: RowPillKindDef[] = TICKET_ATTRIBUTES
    // part 7, 2026-10-11: the sender kind rides inside the one sender pill (switch: sender_organisation),
    // so it is not a kind of its own — the binding stays in TICKET_ATTRIBUTES for the filter and facets.
    .filter(binding => binding.name !== 'sender_kind')
    .map(binding => ({
    id: attributeKindId(binding.name),
    // The tenant's own German noun. Falls back to the served NAME rather than to an invented word:
    // a declaration with no labelDe has told us nothing to translate, and the raw name is at least
    // the thing the tenant called it.
    labelDe: () => {
      const declared = cm.attribute(binding.name);
      return declared && declared.labelDe ? declared.labelDe : binding.name;
    },
    appliesWhen: () => {
      const declared = cm.attribute(binding.name);
      return !!declared && declared.type === 'enum';
    },
  }));

  // IN ROW ORDER — ROW COMPOSITION v1 (part 7 fix loop, 2026-10-12): row 2 (secondary axis ·
  // Neu-Analyse), then row 3 left (plum pills · Frist genannt · Stufe · attributes), then row 3 right
  // (Kopiert · Bearbeitet · Eingang bestätigt). The never-hideable signals between them (the band,
  // the paperclip, the flags, the approved-as badge) are declared in the parity guard.
  return [
    // ── Row 2 ──
    // The second classification axis. Its German noun is SERVED (Prozesstyp / Anliegen / Grund-Code)
    // — spelling one of them here would put one tenant's word on every tenant's settings page, the
    // exact drift the badge-fact ledger exists to shrink.
    { id: 'process_type', labelDe: () => cm.secondaryAxisNoun },
    // The reanalysis trace („N× neu analysiert", muted text since part 7) — on row 2 since
    // 2026-10-12: a note on the classification. Id AND label name what it COUNTS (controller
    // ruling, 2026-09-18): it was boarded as `reminder`, which is a different signal in this product
    // (the triage panel's own chip, off `isReminder`) — re-spelled while nothing was stored against it.
    { id: 'reanalysis', labelDe: () => 'Neuanalysen' },
    // ── Row 3 left: the tenant-exclusive plum pill first ──
    // The sender pill „Organisation · Art". Gated on the same one served attribute every other
    // registry surface is gated on: a tenant with no sender registry has no such pill, so no switch.
    // The LABEL is „Absender" (part 7, 2026-10-11: one noun per fact). The stored ID is untouched
    // (it is a contract with saved preferences); only the German noun moved.
    { id: 'sender_organisation', labelDe: () => 'Absender', appliesWhen: () => !!cm.attribute('sender_kind') },
    // The „Formular" pill (part 7 fix loop, 2026-10-12): the form-extraction tenant's plum pill,
    // hideable like the sender pill. Exists only where the tenant SERVES form extraction (the same
    // capability flag that gates the Excel-batch button) — never a role id.
    { id: 'formular', labelDe: () => FORM_WORD, appliesWhen: () => cm.formExtractionEnabled },
    // ── Row 3 left: the info pills (the flags between are never hideable) ──
    // „Frist genannt": the mail names a deadline (the letter check's quote). Every tenant, hideable
    // like its sibling info pills.
    { id: 'frist_genannt', labelDe: () => FRIST_GENANNT_WORD },
    // The Stufe pill (roman numeral face, small facts on a row 2026-10-19): designed, shipped
    // HIDDEN by default — see DEFAULT_HIDDEN_ROW_PILLS below.
    { id: 'eskalationsstufe', labelDe: () => 'Eskalationsstufe' },
    // The attribute pills close row 3's info group (the utility exception, „Gas").
    ...attributeDefs,
    // ── Row 3 right: the Bearbeitung traces (the approved-as badge before them is never hideable) ──
    // „Antwort kopiert" rather than the badge's own one-word face: the trace has a row's context and
    // a settings list does not, and the one-word literal is the served `decision.copy` face read
    // through `useCategoryMeta().face` (a tenant word, not a settings-page label).
    { id: 'kopiert', labelDe: () => 'Antwort kopiert' },
    { id: 'bearbeitet', labelDe: () => 'Bearbeitet' },
    // The receipt, HIDEABLE beside Kopiert (controller ruling, 2026-09-18): a companion statement
    // about the same row, not a safety signal — the safety class is the flag pill alone. The LABEL is
    // the served `decision.receipt` face (part 4c, 2026-09-27) — the one def that actually reads its
    // `face` argument; the raw-key floor ('receipt') is `resolveFace`'s own honest answer for a
    // vocabulary that declares no such face, never a word this file invents.
    // Exists only where the tenant SERVES the receipt word (small facts on a row, 2026-10-20) — the
    // gate is the served word: no word, no pill, no switch, never the raw key „receipt" as a
    // settings label or a preview pill. While /config is still loading the kind stays listed (its
    // label shimmers), so a settled vocabulary removes nothing that was never drawn.
    { id: 'eingang_bestaetigt', labelDe: face => face('decision', 'receipt') ?? 'receipt', readsFace: true, appliesWhen: () => cm.configLoading || !!cm.servedWord('decision', 'receipt') },
  ];
}

/**
 * THE CODE DEFAULT of „Hinweise in der Liste" — the third and lowest tier of the preference
 * (small facts on a row, operator 2026-10-19): a kind listed here ships HIDDEN on a tenant that
 * states no default of its own. Precedence, each tier replacing the one below it WHOLE (never
 * merged): a clerk's stored preference → the tenant's operator_config UI_ROW_PILLS_HIDDEN → this
 * list (hooks/useRowPillVisibility). Core serves `null` for an ABSENT config row (no default stated)
 * and `[]` for a present-but-blank one (a stated "hide nothing"), since 2026-10-20.
 * Every id here is a registry id above (pinned in __tests__/rowPillKinds.test.ts).
 */
export const DEFAULT_HIDDEN_ROW_PILLS: readonly string[] = ['eskalationsstufe'];

/**
 * THE registry as THIS tenant sees it — every kind whose gate passes, labelled from the served
 * vocabulary, in row order. Kinds whose gate fails are absent, and absent means visible.
 */
export function rowPillKinds(cm: ReturnType<typeof useCategoryMeta>): RowPillKind[] {
  return buildRowPillKindDefs(cm)
    .filter(def => !def.appliesWhen || def.appliesWhen(cm))
    .map(def => ({
      id: def.id,
      // A face-reading def floors to '' while cm.configLoading, never the raw key `resolveFace`
      // would otherwise answer (fix wave 2026-09-27, item 8) — the settings list's own consumer
      // (RowPillSettings) renders the shimmer wherever a label comes back empty.
      labelDe: def.readsFace && cm.configLoading ? '' : def.labelDe(cm.face),
    }));
}

/**
 * The tenant default's storage form: operator_config holds a CSV of hidden ids, and an absent key
 * is an empty list (nothing hidden). Whitespace is tolerated because a human may edit that row.
 */
export const parseHiddenRowPills = (csv: string | null | undefined): string[] =>
  (csv ?? '').split(',').map(part => part.trim()).filter(part => part.length > 0);

/** The inverse — what the Einstellungen draft writes back into the config key. */
export const formatHiddenRowPills = (ids: readonly string[]): string => ids.join(',');

/**
 * The hidden list REBUILT from a visibility decision, in registry order. Order is not cosmetic
 * here: two clerks who hide the same two kinds must produce the same stored string, or the survey
 * and the dirty-check would see a difference that isn't one.
 */
export function withRowPillHidden(
  kinds: readonly RowPillKind[],
  hidden: readonly string[],
  id: string,
  visible: boolean,
): string[] {
  const next = new Set(hidden);
  if (visible) next.delete(id); else next.add(id);
  // Registry order first, then anything stored that this tenant no longer has a kind for — such an
  // id buys nothing on the row, but dropping it here would silently rewrite a preference that may
  // become meaningful again (a vocabulary delivery, a re-enabled attribute).
  const known = kinds.map(k => k.id).filter(k => next.has(k));
  const unknown = [...next].filter(k => !kinds.some(kind => kind.id === k));
  return [...known, ...unknown];
}
